import React, { useState } from "react";
import { Modal } from "@mui/material";
import { useForm } from "react-hook-form";
import { PlusIcon, PencilAltIcon, TrashIcon, XIcon, SearchIcon } from "@heroicons/react/solid";
import Cookies from "universal-cookie";
import { Button, Input } from "../../components/ui";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import Pagination from "../../components/pagination";
import InlineLoader from "../../components/loader/InlineLoader";
import { ROLE } from "../../config";
import { usePettyCashEntities } from "../../hooks/petty_cash/usePettyCashEntities";
import NotAllowed from "../errors/NotAllowed";

const cookies = new Cookies();

function PettyCashEntities() {
  const role = cookies.get(ROLE);

  const {
    entityList,
    filters,
    searchQuery,
    isCreating,
    isUpdating,
    isDeleting,
    handleSearchChange,
    handlePageClick,
    createEntity,
    updateEntity,
    deleteEntity,
  } = usePettyCashEntities();

  const [formModal, setFormModal] = useState({ open: false, edit: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: { name: "" },
  });

  const openCreateModal = () => {
    setValue("name", "");
    setFormModal({ open: true, edit: null });
  };

  const openEditModal = (entity) => {
    setValue("name", entity.name);
    setFormModal({ open: true, edit: entity });
  };

  const closeFormModal = () => {
    setFormModal({ open: false, edit: null });
  };

  const onSubmit = (data) => {
    if (formModal.edit) {
      updateEntity({ entity_id: formModal.edit.entity_id, name: data.name });
    } else {
      createEntity({ name: data.name });
    }
    closeFormModal();
  };

  const entities = entityList.data?.data || [];
  const totalCount = entityList.data?.total_count || 0;

  if (role !== "COMPANY_ADMIN") {
    return <NotAllowed />;
  }

  return (
    <div className="py-6 px-4 md:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <BreadCrumbs
          crumbs={[
            { label: "Dashboard", link: "/" },
            { label: "Entity Master", link: "" },
          ]}
        />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Entity Master</h1>
            <p className="text-gray-500 mt-1">Manage vendors, suppliers, and merchants.</p>
          </div>
          <Button onClick={openCreateModal} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
            <PlusIcon className="w-5 h-5" />
            <span>Add Entity</span>
          </Button>
        </div>

        <RoundedContainer className="p-5 shadow-sm border border-gray-150">
          <div className="relative w-full md:w-80">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by name..."
              className="pl-10 pr-4 py-2 w-full rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm transition-all"
            />
          </div>
        </RoundedContainer>

        {entityList.isLoading ? (
          <div className="flex items-center justify-center py-12 w-full">
            <InlineLoader className="w-8 h-8 text-primary" />
          </div>
        ) : (
          <>
            <RoundedContainer className="hidden md:block border border-gray-150 shadow-sm p-4">
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell>#</TableHeaderCell>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Slug</TableHeaderCell>
                    <TableHeaderCell className="text-center">Actions</TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {entities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-400">
                          No entities found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      entities.map((entity, idx) => (
                        <TableRow key={entity.entity_id}>
                          <TableCell className="text-gray-500">{filters.page_number * filters.limit + idx + 1}</TableCell>
                          <TableCell className="font-semibold text-gray-800">{entity.name}</TableCell>
                          <TableCell className="text-gray-500">{entity.slug}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => openEditModal(entity)}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                                title="Edit"
                              >
                                <PencilAltIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => setDeleteModal({ open: true, id: entity.entity_id })}
                                className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </RoundedContainer>

            <div className="md:hidden space-y-4">
              {entities.length === 0 ? (
                <div className="text-center py-8 text-gray-400 bg-white rounded-2xl border border-gray-150">
                  No entities found.
                </div>
              ) : (
                entities.map((entity) => (
                  <div key={entity.entity_id} className="bg-white rounded-2xl border border-gray-150 p-5 shadow-sm flex flex-col space-y-3">
                    <div>
                      <span className="text-gray-800 font-bold block">{entity.name}</span>
                      <span className="text-xs text-gray-500">{entity.slug}</span>
                    </div>
                    <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => openEditModal(entity)}
                        className="p-2 rounded-xl text-blue-600 bg-blue-50 border border-blue-100 active:scale-95 transition-all"
                        title="Edit"
                      >
                        <PencilAltIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, id: entity.entity_id })}
                        className="p-2 rounded-xl text-red-500 bg-red-50 border border-red-100 active:scale-95 transition-all"
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <RoundedContainer className="border border-gray-150 shadow-sm p-4">
              <Pagination
                currentPage={filters.page_number}
                lengthofItems={totalCount}
                limit={filters.limit}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          </>
        )}
      </div>

      <Modal open={formModal.open} onClose={closeFormModal}>
        <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[50%] max-w-md bg-white rounded-none md:rounded-2xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-150 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-bold text-gray-800">
              {formModal.edit ? "Edit Entity" : "Add Entity"}
            </h3>
            <button onClick={closeFormModal} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors">
              <XIcon className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
            <Input
              control={control}
              label="Entity Name"
              name="name"
              placeholder="Enter entity name"
              rules={{ required: "Entity name is required" }}
            />
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={closeFormModal} disabled={isCreating || isUpdating}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating}>
                {(isCreating || isUpdating) ? (
                  <>
                    <span>Saving...</span>
                    <InlineLoader className="ml-2" />
                  </>
                ) : (
                  formModal.edit ? "Update" : "Create"
                )}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onDelete={() => {
          deleteEntity({ entity_id: deleteModal.id });
          setDeleteModal({ open: false, id: null });
        }}
        isLoading={isDeleting}
        message="Are you sure you want to delete this entity?"
      />
    </div>
  );
}

export default PettyCashEntities;
